import { FC, useState } from 'react';
import shallow from 'zustand/shallow';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Image from 'global/Image';
import Icon from '@ant-design/icons';
import If from 'global/Common/If';
import { formatNumber } from 'utils/number';
import tradingDataStore from 'lib/stores/tradingData';
import SplitLotRow from 'global/Securities/components/Table/SplitLotRow';
import { isRedirectToNewCore } from 'features/securities/utils';
import useCashInfoStore from 'global/Securities/stores/useCashInfoStore';
import {
  expiryOptions,
  orderTypeOptions,
} from 'global/Securities/constants/transactionForm';
import Chips from 'global/Securities/components/Chips';
import { LightningSvg } from 'features/day-trade/components/Icons';
import { SPLIT_ORDER_COPY } from 'global/Securities/constants/transaction';
import {
  BuyOrderType,
  SplitOrderMethodEnum,
} from 'global/Securities/types/transaction';
import { iPreviewData } from 'typings/transactionForm';
import { generateBuyFeeData } from '../../utils/generateFeeData';
import generateInvestmentBalance from '../../utils/generateInvestmentBalance';
import {
  PreviewContainer,
  PreviewItem,
  PreviewItemRow,
  InvestmentWrapper,
  InvestmentDetail,
} from '../../styled';
import { expiryOption, lotSize } from '../../constant';
import realTradingStore from '../store';

interface BuyPreviewProps {
  showExchangeFee: () => void;
  data: iPreviewData;
}

const BuyPreview: FC<BuyPreviewProps> = ({ showExchangeFee, data }) => {
  const [openInvestment, setOpenInvestment] = useState<boolean>(false);

  const {
    configFormula: { data: configFormula },
  } = tradingDataStore();

  const {
    price,
    shares,
    triggerPrice,
    gtc,
    symbol,
    isSplitOrderActive,
    splitQuantity,
    splitRangeMin,
    splitRangeMax,
    multiplier,
  } = data;

  const expiry = expiryOption[gtc];
  const dayTradeMultiplier = Number(multiplier);
  const type = data.type === 'Auto Buy' ? BuyOrderType.AutoBuy : data.type;
  const isMarket = type === BuyOrderType.MarketOrder;
  const isAutoBuy = type === BuyOrderType.AutoBuy;
  const isLimit = type === BuyOrderType.LimitOrder;
  const isDayTrade = data.isDayTrade && isLimit;

  const { splitMethod } = realTradingStore();

  const dayTradeLeverage = useCashInfoStore((s) => s.dayTradeLeverage, shallow);

  const lot = shares / lotSize;
  const { investmentPlusFee, brokerFee, exchangeFee } = generateBuyFeeData(
    configFormula,
    lot,
    price,
    isDayTrade,
  );

  const { totalInvestment, investmentDayTrade, investmentTradingBalance } =
    generateInvestmentBalance(
      configFormula,
      price,
      lot,
      dayTradeMultiplier,
      dayTradeLeverage,
    );

  return (
    <PreviewContainer direction="column" width="100%">
      <PreviewItem>
        <Text>Stock</Text>
        <Text family="bold">{symbol}</Text>
      </PreviewItem>

      <PreviewItem>
        <Text>Order Type</Text>
        <Text>{orderTypeOptions.Buy[type]}</Text>
      </PreviewItem>

      <PreviewItem>
        <Text>Expiry</Text>
        <Text>{isMarket ? 'Fill and Kill' : expiryOptions[expiry]}</Text>
      </PreviewItem>

      <If condition={isDayTrade}>
        <PreviewItem>
          <Text>Leverage</Text>

          <PreviewItemRow>
            <Chips>
              <Icon component={LightningSvg} />

              {`${dayTradeMultiplier}x`}
            </Chips>
            <Text>Day Trade</Text>
          </PreviewItemRow>
        </PreviewItem>
      </If>

      {isAutoBuy && (
        <PreviewItem>
          <Text>Trigger Price</Text>
          <Text>{triggerPrice}</Text>
        </PreviewItem>
      )}

      <If condition={!isMarket}>
        <PreviewItem>
          <Text>{isAutoBuy && 'Buy '} Price</Text>
          <Text>{formatNumber(price, false)}</Text>
        </PreviewItem>
      </If>

      <SplitLotRow
        lot={lot}
        disabled={isSplitOrderActive || !isRedirectToNewCore()}
      />

      {isSplitOrderActive && (
        <>
          <PreviewItem>
            <Text>Split Quantity</Text>
            <Text>{formatNumber(splitQuantity, false)}</Text>
          </PreviewItem>
          <PreviewItem>
            <Text>Split Method</Text>
            <Text>{SPLIT_ORDER_COPY[splitMethod]}</Text>
          </PreviewItem>
          <If condition={splitMethod === SplitOrderMethodEnum.RANDOM}>
            <PreviewItem>
              <Text>Split Range Lot</Text>
              <Text>
                {`${formatNumber(splitRangeMin, false)} - ${formatNumber(
                  splitRangeMax,
                  false,
                )}`}
              </Text>
            </PreviewItem>
          </If>
        </>
      )}

      <PreviewItem>
        <Flex
          align="center"
          onClick={() => {
            if (!isDayTrade) return;
            setOpenInvestment(!openInvestment);
          }}
        >
          <Text>{isMarket ? 'Estimated ' : ''} Investment</Text>

          <If condition={isDayTrade}>
            <Image
              src={`/icon/chevron-${!openInvestment ? 'down' : 'up'}.svg`}
              alt="dropdown"
              style={{ marginLeft: '5px', width: '16px', cursor: 'pointer' }}
            />
          </If>
        </Flex>
        <Text>Rp {formatNumber(totalInvestment, false)}</Text>
      </PreviewItem>

      <If condition={isDayTrade && openInvestment}>
        <InvestmentWrapper>
          <InvestmentDetail>
            <PreviewItem>
              <Text>Trading Balance</Text>
              <Text>Rp {formatNumber(investmentTradingBalance, false)}</Text>
            </PreviewItem>

            <PreviewItem>
              <Text>Day Trade</Text>
              <Text>Rp {formatNumber(investmentDayTrade, false)}</Text>
            </PreviewItem>
          </InvestmentDetail>
        </InvestmentWrapper>
      </If>

      <PreviewItem>
        <Text>{isMarket ? 'Estimated ' : ''}Broker Fee</Text>
        <Text>Rp {formatNumber(brokerFee)}</Text>
      </PreviewItem>

      <PreviewItem>
        <Flex align="center">
          <Text>{isMarket ? 'Estimated ' : ''}Exchange Fee</Text>
          <Image
            src="/icon/information.svg"
            alt="fee-info"
            onClick={showExchangeFee}
            style={{ marginLeft: '5px', width: '12px', cursor: 'pointer' }}
          />
        </Flex>
        <Text>Rp {formatNumber(exchangeFee, false)}</Text>
      </PreviewItem>

      <PreviewItem>
        <div>
          <Text family="bold">
            {isMarket ? 'Total Estimated ' : ''}Investment (Plus Fee)
          </Text>
        </div>
        <Text family="bold">Rp {formatNumber(investmentPlusFee)}</Text>
      </PreviewItem>
    </PreviewContainer>
  );
};

export default BuyPreview;
